import type { jsPDF } from "jspdf";
import { canv } from "@/canv/global";
import { CommentWidget } from "@/elements/comment";
import { createImage, getLodByResolution, imageWidget } from "@/elements/image";
import { Group } from "@/group/group";
import { findFirstFrameWidget } from "@/tools/cursorTool/utils";

export const getDocSize = (doc: jsPDF, visualWidth: number, visualHeight: number) => {
    const docWidth = doc.internal.pageSize.getWidth();
    const docHeight = doc.internal.pageSize.getHeight();

    const imageRatio = visualHeight / visualWidth;
    const docRatio = docHeight / docWidth;

    let width = 0;
    let height = 0;
    let x = 0;
    let y = 0;

    if (imageRatio > docRatio) {
        height = docHeight;
        width = height / imageRatio;
        x = (docWidth - width) / 2;
    } else {
        width = docWidth;
        height = width * imageRatio;
        y = (docHeight - height) / 2;
    }

    return { x, y, width, height };
};

export const queryAreaForExport = (area: Pick<Group, "startX" | "startY" | "width" | "height">) => {
    if (!canv.canvasController.hashGrid) return [];

    const areaCells = canv.canvasController.hashGrid.getCellsForElement(area);
    const found = new Set<Group>();

    const isIntersectingArea = (currentGroup: Group) => {
        const areaRight = area.startX + area.width;
        const areaBottom = area.startY + area.height;

        const groupRight = currentGroup.startX + currentGroup.width;
        const groupBottom = currentGroup.startY + currentGroup.height;

        return !(
            groupRight < area.startX ||
            currentGroup.startX > areaRight ||
            groupBottom < area.startY ||
            currentGroup.startY > areaBottom
        );
    };

    for (const cell of areaCells) {
        const cellGroups = canv.canvasController.hashGrid.grid.get(cell);
        if (!cellGroups) continue;

        for (const group of cellGroups) {
            if (!isIntersectingArea(group)) continue;

            const hasExclude = group.children.some((child) => child instanceof CommentWidget);

            if (!hasExclude) found.add(group);
        }
    }

    return Array.from(found);
};

export const mapGroupsWithoutFrameToFrames = (
    groupsWithFrames: Group[],
    groupsWithoutFrame: Group[],
): Record<string, Group[]> => {
    const result: Record<string, Group[]> = {};

    for (const frameGroup of groupsWithFrames) {
        const frameWidget = findFirstFrameWidget(frameGroup);
        if (!frameWidget) continue;

        const frameArea = frameWidget.getArea();
        const matchingGroups: Group[] = [];

        for (const otherGroup of groupsWithoutFrame) {
            if (otherGroup.isAnyItemInArea(frameArea)) {
                matchingGroups.push(otherGroup);
            }
        }

        if (!matchingGroups.length && frameWidget.overlappedGroups.length) {
            matchingGroups.push(...frameWidget.getOverlappedGroups());
        }

        if (matchingGroups.length) {
            result[frameGroup.UID] = matchingGroups;
        }
    }

    return result;
};

export const sortFramesByPosition = (groupsWithFrame: Group[]) => {
    groupsWithFrame.sort((a, b) => {
        // Сначала сравниваем по Y (чем меньше, тем выше)
        if (a.startY !== b.startY) {
            return a.startY - b.startY;
        }
        // Если Y одинаковый — сравниваем по X (чем меньше, тем левее)
        return a.startX - b.startX;
    });
};

export async function prepareAllImagesForExport(groups: Group[]) {
    const preparePromises: Promise<Element>[] = [];

    for (const rootGroup of groups) {
        const stack: Group[] = [rootGroup];

        while (stack.length > 0) {
            const current = stack.pop();

            if (!current || !current.children) continue;

            for (const child of current.children) {
                if (child instanceof imageWidget) {
                    const lod = getLodByResolution(child);
                    const bitmap = child.bitmapLODs.get(lod);
                    if (!bitmap && child.src) {
                        if (child.image) {
                            preparePromises.push(child.generateBitmap(child.image, lod));
                        } else {
                            const url = child.prepareImageUrl(false);
                            const image = await createImage(url, true);
                            child.image = image;
                            preparePromises.push(child.generateBitmap(image, lod));
                        }
                    }
                } else if (child instanceof Group) {
                    stack.push(child);
                }
            }
        }
    }

    await Promise.all(preparePromises);
}
